import React, {useEffect, useState} from 'react';
import * as yup from 'yup';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import InputMUI from '../../../../shared/InputMUI';
import ButtonMUI from '../../../../shared/ButtonMUI';
import SelectComponent from '../../../../shared/SelectComponent';
import {useDispatch, useSelector} from 'react-redux';
import './CustomerAdd.scss';
import {getUserRoles} from '../../../../redux/actions/profile/profileActions';
import {useTranslation} from 'react-i18next';
import {notifySuccess} from '../../../../helpers/notifySnack';
import {getCompaniesClientsList, postClients} from '../../../../redux/actions/clients/clientsActions';
import {getCompany} from '../../../../redux/actions/company/companyActions';
import CompaniesHero from '../CompaniesHero';
import {mainPath} from '../../../../routes/paths';
import {useHistory} from 'react-router-dom';
import {passwordRegex} from "../../../../helpers/constants";

const CustomerAdd = () => {
    const {t} = useTranslation();
    const history = useHistory();
    const dispatch = useDispatch();
    const {buttonLoading} = useSelector(({app}) => app);
    const [buttonLoad, setButtonLoad] = useState(false);
    const {singleCompany} = useSelector((state) => state.company);
    const {companiesClientsList, companiesClientsListLoading} = useSelector((state) => state.clients);

    const {userRoles} = useSelector((state) => state.profile);

    useEffect(() => {
        if (companiesClientsListLoading && !companiesClientsList.length && singleCompany?.id) {
            dispatch(getCompaniesClientsList(singleCompany.id));
        }
    }, [dispatch, singleCompany.id, companiesClientsListLoading]);

    useEffect(() => {
        dispatch(getUserRoles('client'));
    }, [dispatch]);

    const position = userRoles.map((role) => ({
        id: role.id,
        role: role.role,
        name: role.name
    }));

    const schema = yup.object({
        position: yup.string().required(t('validations.required')),
        firstName: yup.string().required(t('validations.required')),
        phone: yup.string().required(t('validations.required')),
        email: yup.string().email(t('validations.email')).required(t('validations.required')),
        password: yup.string().matches(passwordRegex, t('validations.password'))
            .required('validations.required'),
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: {errors, isValid, isSubmitting}
    } = useForm({
        mode: 'all',
        reValidateMode: 'onChange',
        shouldFocusError: true,
        resolver: yupResolver(schema),
        defaultValues: {
            position: '',
            firstName: '',
            phone: '',
            email: '',
            password: ''
        }
    });

    const onSubmit = async (data) => {
        setButtonLoad(true);
        try {
            const selectedRole = position.find((role) => role.id === data.position);

            if (!selectedRole) {
                throw new Error('Role not found');
            }

            const formData = new FormData();
            formData.append('companyId', singleCompany?.id);
            formData.append('position', selectedRole.role);
            formData.append('firstName', data.firstName);
            formData.append('phone', data.phone);
            formData.append('email', data.email);
            formData.append('password', data.password);

            const res = await dispatch(postClients(formData));

            if (res.payload?.status === 201) {
                setButtonLoad(false);
                dispatch(getCompaniesClientsList(singleCompany?.id));
                dispatch(getCompany(singleCompany?.id));
                notifySuccess(t('all.successfully'));
                reset({
                    position: '',
                    firstName: '',
                    phone: '',
                    email: '',
                    password: ''
                });
                history.push(mainPath.companiesCustomers.replace(':id', singleCompany?.id));
            } else {
                setButtonLoad(false);
            }
        } catch (e) {
            console.log(e);
            setButtonLoad(false);
        }
    };

    const handleBack = () => {
        if (history.length > 1) {
            history.goBack();
        } else {
            history.push(mainPath.companiesCustomers.replace(':id', singleCompany?.id));
        }
    };

    return (
        <div className='companies-customers-wrapper'>
            <div className='companies-customers'>
                <div className='companies-name'>{singleCompany?.name ?? ''}</div>

                <CompaniesHero
                    heading={`${companiesClientsList.length} ${t('CompaniesCustomers.clients_count')}`}
                    subHeading={t('CompaniesCustomers.clients')}
                    selectedBack={true}
                    selectedList={false}
                    selectedAdd={false}
                    handleList={handleBack}
                />

                <div className='customer-add'>
                    <form className='customer-add__form' onSubmit={handleSubmit(onSubmit)}>
                        <div className='customer-add__title'>{t('ProjectsParticipantsAdd.register')}</div>

                        <Controller
                            name='position'
                            control={control}
                            render={({field}) => (
                                <SelectComponent
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    value={position.find((c) => c.name === field.value)}
                                    options={position}
                                    label={t('ProjectsParticipantsAdd.role')}
                                    placeholder=''
                                    inputProps={field}
                                />
                            )}
                        />

                        <div className='form__input'>
                            <Controller
                                name='firstName'
                                control={control}
                                render={({field}) => (
                                    <InputMUI
                                        type='text'
                                        fullWidth
                                        className='no-icons'
                                        error={errors.firstName?.message}
                                        inputProps={field}
                                        label={t('ProjectsParticipantsAdd.firstName')}
                                    />
                                )}
                            />
                        </div>

                        <div className='form__input'>
                            <Controller
                                name='phone'
                                control={control}
                                render={({field, fieldState: {error}}) => (
                                    <InputMUI
                                        className='no-icons'
                                        type='tel'
                                        fullWidth
                                        error={error?.message}
                                        label={t('ProjectsParticipantsAdd.phone')}
                                        onChange={(e) => {
                                            const value = e.target.value;

                                            let cleanValue = value.replace(/[^0-9+]/g, ''); // Remove anything that's not a digit or '+'

                                            if (cleanValue.charAt(0) !== '+') {
                                                cleanValue = cleanValue.replace(/\+/g, ''); // Remove any '+' that is not at the start
                                            } else {
                                                cleanValue = '+' + cleanValue.replace(/\+/g, ''); // Keep only the first '+' and remove others
                                            }

                                            if (/^\+?\d*$/.test(cleanValue)) {
                                                field.onChange(cleanValue);
                                            }
                                        }}
                                        inputProps={field}
                                    />
                                )}
                            />
                        </div>

                        <div className='form__input'>
                            <Controller
                                name='email'
                                control={control}
                                render={({field}) => (
                                    <InputMUI
                                        type='email'
                                        fullWidth
                                        className='no-icons'
                                        error={errors.email?.message}
                                        inputProps={field}
                                        label='E-mail'
                                    />
                                )}
                            />
                        </div>

                        <div className='form__input'>
                            <Controller
                                name='password'
                                control={control}
                                render={({field}) => (
                                    <InputMUI
                                        type='password'
                                        fullWidth
                                        className='no-icons'
                                        error={errors.password?.message}
                                        inputProps={{
                                            ...field,
                                            role: 'presentation',
                                            autoComplete: 'new-password',
                                        }}
                                        label={t('ProjectsParticipantsAdd.password')}
                                    />
                                )}
                            />
                        </div>

                        <ButtonMUI disabled={!isValid || buttonLoading || isSubmitting} loading={buttonLoad} formAction>
                            {t('ProjectsParticipantsAdd.add')}
                        </ButtonMUI>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CustomerAdd;
